import React, { useContext, useEffect, useState } from 'react'
import { Table, Ellipsis, Message } from '@cx/ui'
import { useRequest } from 'ahooks'
import { AuthContext } from '@/layout/Auth'
import { getAPIToken, putApiToken, deleteAPIToken } from '@/api'
import { Box, Stack, Button } from '@mui/material'
import APITokenModal from './APITokenModal'
import Modal2 from '@/components/Modal2'
import EditPermissionModal from './EditPermissionModal'
import defaultAvatar from '@/asset/img/default_avatar.png'
import { Card, Text } from './common'
import { ensureSecurityGate } from '@/utils/securityGate'

const APIToken = () => {
  const { authInfo } = useContext(AuthContext)
  const orgInfo = authInfo?.orgInfo
  const [visible, setVisible] = useState(false)
  const [tokenInfo, setTokenInfo] = useState<any>({})
  const [editPermissionVisible, setEditPermissionVisible] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const {
    data: { data: dataSource, total } = { data: [], total: 0 },
    loading,
    run,
  } = useRequest(
    (p = page, size = pageSize) =>
      getAPIToken({
        id: orgInfo?.id!,
        query: { page: p, size },
      }),
    {
      manual: true,
    },
  )

  const changePage = (page: number, pageSize: number) => {
    setPage(page)
    setPageSize(pageSize)
  }

  const onDeleteToken = (id: number) => {
    Modal2.confirm({
      title: '删除 API Token',
      content: '确定删除该 API Token 吗？',
      okButtonProps: { color: 'error' },
      onOk: async () => {
        return deleteAPIToken({ id, org_id: orgInfo?.id! }).then(() => {
          Message.success('删除成功')
          setPage(1)
          run(1)
        })
      },
    })
  }

  const onPutApiToken = (data: { id: number; org_id: number; perms: number[] }) => {
    return putApiToken(data).then(() => {
      Message.success('修改成功')
      setPage(1)
      run(1)
    })
  }

  useEffect(() => {
    if (orgInfo?.id) {
      run()
    }
  }, [orgInfo?.id, page, pageSize])

  const columns = [
    {
      dataIndex: 'token',
      title: 'API Token',
      width: 300,
      render(value: string, record: any) {
        return <Ellipsis>{value || '-'}</Ellipsis>
      },
    },

    {
      dataIndex: 'name',
      title: '备注',
      width: 220,
      render(value: string, record: any) {
        return <Ellipsis>{value || '-'}</Ellipsis>
      },
    },
    {
      dataIndex: 'creator_mail',
      title: '创建者',
      render(value: string, record: any) {
        return (
          <Stack direction='row' alignItems='center' gap={1}>
            <Box
              component='img'
              sx={{ width: 22, height: 22, borderRadius: '50%' }}
              src={record.creator_headimgurl || defaultAvatar}
            />
            <Ellipsis sx={{ width: 'calc(100% - 32px)', pt: '2px' }}>{record.creator_name || value || '-'}</Ellipsis>
          </Stack>
        )
      },
    },
    {
      dataIndex: 'opt',
      title: '操作',
      width: 180,
      render(_: null, record: any) {
        return (
          <>
            <Button
              size='small'
              onClick={async () => {
                if (!(await ensureSecurityGate('api_token'))) return
                setTokenInfo(record)
                setTimeout(() => {
                  setEditPermissionVisible(true)
                }, 0)
              }}
            >
              修改权限
            </Button>
            <Button
              color='error'
              size='small'
              onClick={async () => {
                if (!(await ensureSecurityGate('api_token'))) return
                onDeleteToken(record.id)
              }}
            >
              删除
            </Button>
          </>
        )
      },
    },
  ]
  return (
    <Card>
      <APITokenModal
        open={visible}
        onCancel={() => setVisible(false)}
        onOk={() => {
          setPage(1)
          run(1)
        }}
      />
      <EditPermissionModal
        value={tokenInfo.perms || []}
        open={editPermissionVisible}
        onCancel={() => {
          setEditPermissionVisible(false)
        }}
        onOk={(perms) => {
          onPutApiToken({ id: tokenInfo.id, org_id: orgInfo?.id!, perms })
        }}
      />
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 3 }}>
        <Text>API Token</Text>
        <Button
          variant='contained'
          onClick={async () => {
            if (!(await ensureSecurityGate('api_token'))) return
            setVisible(true)
          }}
        >
          生成 API Token
        </Button>
      </Stack>

      <Table
        loading={loading}
        columns={columns}
        dataSource={dataSource || []}
        PaginationProps={{
          sx: { m: 3 },
        }}
        sx={{
          mx: -3,
          '.MuiTableCell-root': {
            pl: 0,
            '&:first-of-type': {
              pl: 3,
            },
          },
        }}
        pagination={{
          sx: { mt: 2 },
          total,
          page,
          pageSize,
          onChange: changePage,
        }}
      />
    </Card>
  )
}

export default APIToken
